import "../css/GalleryPageComponent.scss";
import BannerComponent from "./BannerComponent";
import GalleryBannerImage from '../assets/WhatsApp Image 2024-09-23 at 4.44.24 PM 1.png';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { IoClose } from 'react-icons/io5';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

// School Activities
import SchoolActivityBags2 from '../assets/school-activity-bags-2.jpg';
import SchoolActivityBooks from '../assets/school-activity-books.jpg';
import SchoolActivityBagsAll from '../assets/school-activity-bags-all.jpg';
import SchoolActivityGroup from '../assets/school-activity-group.jpg';
import SchoolActivityOutdoor from '../assets/school-activity-outdoor.jpg';

// Extra Curricular
import OddSocksDay from '../assets/odd-socks-day.jpg';
import SportsdayTugOfWar from '../assets/sportsday-tug-of-war.jpg';
import SportsdayHouseBanner from '../assets/sportsday-house-banner.jpg';
import SportsdayTrophyWinners from '../assets/sportsday-trophy-winners.jpg';
import SportsdayBallRelay from '../assets/sportsday-ball-relay.jpg';

// Classroom
import NurseryClassroom from '../assets/nursery-classroom.jpg';
import ClassroomWhiteboardLesson from '../assets/classroom-whiteboard-lesson.jpg';
import ClassroomCraftActivity from '../assets/classroom-craft-activity.jpg';

// School Trips
import LawizaShopTour from '../assets/lawiza-shop-tour.jpg';
import LawizaMannequinDemo from '../assets/lawiza-mannequin-demo.jpg';
import LawizaDesignerExplains from '../assets/lawiza-designer-explains.jpg';
import LawizaGarmentShowcase from '../assets/lawiza-garment-showcase.jpg';

// Facilities
import FacilityLibrary from '../assets/facility-library.jpg';
import FacilityPrimaryClassroom from '../assets/facility-primary-classroom.jpg';
import FacilityNurseryClassroom from '../assets/facility-nursery-classroom.jpg';
import FacilityPlayArea from '../assets/facility-play-area.jpg';
import FacilityComputerLab from '../assets/facility-computer-lab.jpg';

// Graduation
import GraduationMcTrio from '../assets/graduation-mc-trio.jpg';
import GraduationSashes from '../assets/graduation-sashes.jpg';
import GraduationGroup from '../assets/graduation-group.jpg';
import GraduationDirectorCake from '../assets/graduation-director-cake.jpg';

interface GalleryImage { src: string; alt: string; position?: string; }
interface GallerySection { id: string; title: string; description: string; images: GalleryImage[]; }

const sections: GallerySection[] = [
  {
    id: 'school-activities',
    title: 'School Activities',
    description: 'Highlights from special school programmes, community partnerships, and memorable events that enrich student life at HoneyRock.',
    images: [
      { src: SchoolActivityBags2, alt: 'HoneyRock students and guests in a group photo at the school compound', position: 'center 40%' },
      { src: SchoolActivityBooks, alt: 'A HoneyRock student playing giant chess on the school astroturf', position: 'center 50%' },
      { src: SchoolActivityBagsAll, alt: 'HoneyRock students in a mentoring session with their headteacher', position: 'center 35%' },
      { src: SchoolActivityGroup, alt: 'Students taking notes during a session with the school headteacher', position: 'center 35%' },
      { src: SchoolActivityOutdoor, alt: 'Teachers and students enjoying outdoor activities in the school compound', position: 'center 45%' },
    ],
  },
  {
    id: 'extracurricular',
    title: 'Extra Curricular Activities',
    description: 'Explore the wide range of activities that help our students develop skills beyond the classroom.',
    images: [
      { src: OddSocksDay, alt: 'Students celebrating Odd Socks Day', position: 'center center' },
      { src: SportsdayTugOfWar, alt: 'Staff and parents in a tug of war on Sports Day', position: 'center 65%' },
      { src: SportsdayHouseBanner, alt: 'Students holding the Sports Day house banner', position: '30% center' },
      { src: SportsdayTrophyWinners, alt: 'Students celebrating with their Sports Day trophies', position: 'center 55%' },
      { src: SportsdayBallRelay, alt: 'Students competing in a ball relay on Sports Day', position: 'center 60%' },
    ],
  },
  {
    id: 'classroom',
    title: 'Classroom Photos',
    description: 'Experience our dynamic and engaging classrooms where students actively participate and thrive.',
    images: [
      { src: NurseryClassroom, alt: 'Nursery students learning in a colourful classroom corner', position: 'center 70%' },
      { src: ClassroomWhiteboardLesson, alt: 'Primary students seated at their desk during a lesson', position: 'center 65%' },
      { src: ClassroomCraftActivity, alt: 'Students collaborating on a craft activity in class', position: 'center 40%' },
    ],
  },
  {
    id: 'trips',
    title: 'School Trips & Excursions',
    description: 'Exciting educational trips that broaden our students\' horizons and real-world experiences.',
    images: [
      { src: LawizaShopTour, alt: 'Students on an excursion to the Lawiza fashion studio', position: 'center 40%' },
      { src: LawizaMannequinDemo, alt: 'A designer demonstrating garment-making to students at Lawiza', position: 'center 35%' },
      { src: LawizaDesignerExplains, alt: 'A designer at Lawiza showing students a handmade garment', position: 'center 25%' },
      { src: LawizaGarmentShowcase, alt: 'A student examining a designer garment at Lawiza', position: 'center 20%' },
    ],
  },
  {
    id: 'facilities',
    title: 'Facilities Tour',
    description: 'A glimpse of our well-equipped facilities designed to provide the best learning environment for every child.',
    images: [
      { src: FacilityLibrary, alt: 'The HoneyRock School library with blue study desks and bookshelves', position: 'center center' },
      { src: FacilityNurseryClassroom, alt: 'Colourful nursery classroom with round tables and learning materials', position: 'center center' },
      { src: FacilityPlayArea, alt: 'Outdoor play area with colourful toys and astroturf', position: 'center center' },
      { src: FacilityComputerLab, alt: 'Computer lab with desktop workstations for students', position: 'center center' },
      { src: FacilityPrimaryClassroom, alt: 'Bright primary school classroom with whiteboard and desks', position: 'center center' },
    ],
  },
  {
    id: 'graduation',
    title: 'Graduation & Special Events',
    description: 'Celebrating the milestones and proud moments that mark our students\' journeys with us.',
    images: [
      { src: GraduationMcTrio, alt: 'Graduating students on stage at the Blazing Stars graduation ceremony', position: 'center 25%' },
      { src: GraduationSashes, alt: 'Graduands wearing their HoneyRock Graduand sashes', position: 'center 35%' },
      { src: GraduationGroup, alt: 'Students celebrating at the graduation ceremony', position: 'center 35%' },
      { src: GraduationDirectorCake, alt: 'The Director celebrating with graduands and their cakes', position: 'center 40%' },
    ],
  },
];

const VISIBLE_COUNT = 3;

interface ModalState { sectionIdx: number; imageIdx: number; }

const GalleryPageComponent = () => {
  const [modal, setModal] = useState<ModalState | null>(null);
  const touchStartX = useRef(0);

  const openModal = useCallback((sectionIdx: number, imageIdx: number) => {
    setModal({ sectionIdx, imageIdx });
    document.body.style.overflow = 'hidden';
  }, []);

  const closeModal = useCallback(() => {
    setModal(null);
    document.body.style.overflow = '';
  }, []);

  const navigate = useCallback((dir: 1 | -1) => {
    setModal(prev => {
      if (!prev) return prev;
      const total = sections[prev.sectionIdx].images.length;
      return { ...prev, imageIdx: (prev.imageIdx + dir + total) % total };
    });
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
      else if (e.key === 'ArrowLeft') navigate(-1);
      else if (e.key === 'ArrowRight') navigate(1);
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [closeModal, navigate]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) navigate(diff > 0 ? 1 : -1);
  };

  return (
    <div>
      <BannerComponent
        title='A glimpse into our school life'
        image={GalleryBannerImage}
        description="Explore the vibrant atmosphere, special moments, and everyday experiences that make our school unique."
        buttonInformation={<></>}
      />

      <div className="page-width">
        <div className="gallery-section">
          {sections.map((section, sIdx) => {
            const visible = section.images.slice(0, VISIBLE_COUNT);
            const hiddenCount = Math.max(0, section.images.length - VISIBLE_COUNT);

            return (
              <div key={section.id} className="gallery-section-block">
                <div className="section-header">
                  <header>{section.title}</header>
                  <p>{section.description}</p>
                </div>

                <div className="photos-grid">
                  {visible.map((img, vIdx) => {
                    const isLastSlot = vIdx === visible.length - 1;
                    const showOverlay = isLastSlot && hiddenCount > 0;
                    return (
                      <div
                        key={vIdx}
                        className="photo-slot"
                        onClick={() => openModal(sIdx, showOverlay ? VISIBLE_COUNT : vIdx)}
                        role="button"
                        aria-label={showOverlay ? `View ${hiddenCount} more photos` : img.alt}
                      >
                        <img
                          src={img.src}
                          alt={img.alt}
                          style={img.position ? { objectPosition: img.position } : undefined}
                        />
                        {showOverlay && (
                          <div className="more-overlay">
                            <span>+{hiddenCount}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {modal && (() => {
        const section = sections[modal.sectionIdx];
        const img = section.images[modal.imageIdx];
        const total = section.images.length;
        return (
          <div
            className="gallery-modal"
            onClick={closeModal}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            role="dialog"
            aria-modal="true"
            aria-label={`${section.title} - image ${modal.imageIdx + 1} of ${total}`}
          >
            <button className="modal-close" onClick={closeModal} aria-label="Close gallery">
              <IoClose />
            </button>

            {total > 1 && (
              <button
                className="modal-nav modal-prev"
                onClick={e => { e.stopPropagation(); navigate(-1); }}
                aria-label="Previous image"
              >
                <FiChevronLeft />
              </button>
            )}

            <img
              src={img.src}
              alt={img.alt}
              onClick={e => e.stopPropagation()}
            />

            {total > 1 && (
              <button
                className="modal-nav modal-next"
                onClick={e => { e.stopPropagation(); navigate(1); }}
                aria-label="Next image"
              >
                <FiChevronRight />
              </button>
            )}

            <div className="modal-counter">{modal.imageIdx + 1} / {total}</div>
          </div>
        );
      })()}
    </div>
  );
};

export default GalleryPageComponent;
